import { Link } from 'react-router-dom';

interface Question {
  id: number;
  section: 'rw' | 'math';
  domain: string;
  subtopic: string;
  question: string;
  options: string[];
  answer: string;
  explanation: string;
  passage?: string;
}

interface FullExamResultsProps {
  questions: Question[];
  userAnswers: (string | null)[];
  timeUsed: number;
  onReview: () => void;
  onRetake: () => void;
}

export default function FullExamResults({
  questions,
  userAnswers,
  timeUsed,
  onReview,
  onRetake,
}: FullExamResultsProps) {
  const totalQuestions = questions.length;
  const correctAnswers = questions.filter((q, i) => userAnswers[i] === q.answer).length;
  const scorePercentage = Math.round((correctAnswers / totalQuestions) * 100);

  const rwQuestions = questions.filter(q => q.section === 'rw');
  const mathQuestions = questions.filter(q => q.section === 'math');

  const rwCorrect = rwQuestions.filter((q, i) => userAnswers[i] === q.answer).length;
  const mathCorrect = mathQuestions.filter((q) => {
    const originalIndex = questions.findIndex(qu => qu.id === q.id);
    return userAnswers[originalIndex] === q.answer;
  }).length;

  const rwScore = Math.round((rwCorrect / rwQuestions.length) * 100);
  const mathScore = Math.round((mathCorrect / mathQuestions.length) * 100);

  const domainStats = questions.reduce((acc, q, i) => {
    if (!acc[q.domain]) {
      acc[q.domain] = { total: 0, correct: 0 };
    }
    acc[q.domain].total++;
    if (userAnswers[i] === q.answer) {
      acc[q.domain].correct++;
    }
    return acc;
  }, {} as Record<string, { total: number; correct: number }>);

  const weakAreas = Object.entries(domainStats)
    .map(([domain, stats]) => ({
      domain,
      percentage: Math.round((stats.correct / stats.total) * 100),
      correct: stats.correct,
      total: stats.total,
    }))
    .filter(area => area.percentage < 70)
    .sort((a, b) => a.percentage - b.percentage);

  const getDomainColor = (domain: string) => {
    const colors: Record<string, string> = {
      'Information & Ideas': 'bg-blue-100 text-blue-700 border-blue-300',
      'Craft & Structure': 'bg-purple-100 text-purple-700 border-purple-300',
      'Expression of Ideas': 'bg-pink-100 text-pink-700 border-pink-300',
      'Standard English Conventions': 'bg-rose-100 text-rose-700 border-rose-300',
      'Algebra': 'bg-emerald-100 text-emerald-700 border-emerald-300',
      'Advanced Math': 'bg-cyan-100 text-cyan-700 border-cyan-300',
      'Problem-Solving & Data Analysis': 'bg-amber-100 text-amber-700 border-amber-300',
      'Geometry & Trigonometry': 'bg-violet-100 text-violet-700 border-violet-300',
    };
    return colors[domain] || 'bg-slate-100 text-slate-700 border-slate-300';
  };

  const getScoreColor = (percentage: number) => {
    if (percentage >= 80) return 'text-emerald-600';
    if (percentage >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 py-12 px-6">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-indigo-500 to-sky-500 mb-6">
            <i className="ri-trophy-line text-4xl text-white"></i>
          </div>
          <h1 className="text-4xl font-bold text-slate-800 mb-3">Full SAT Mock Exam Complete!</h1>
          <p className="text-lg text-slate-600">Here's how you performed across all sections</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
            <div className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">Overall Score</div>
            <div className={`text-5xl font-bold mb-2 ${getScoreColor(scorePercentage)}`}>{scorePercentage}%</div>
            <div className="text-sm text-slate-600">
              {correctAnswers} / {totalQuestions} correct
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
            <div className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">R&W Score</div>
            <div className={`text-5xl font-bold mb-2 ${getScoreColor(rwScore)}`}>{rwScore}%</div>
            <div className="text-sm text-slate-600">
              {rwCorrect} / {rwQuestions.length} correct
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
            <div className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">Math Score</div>
            <div className={`text-5xl font-bold mb-2 ${getScoreColor(mathScore)}`}>{mathScore}%</div>
            <div className="text-sm text-slate-600">
              {mathCorrect} / {mathQuestions.length} correct
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-8 mb-8">
          <h2 className="text-xl font-bold text-slate-800 mb-6">Performance by Domain</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(domainStats).map(([domain, stats]) => {
              const percentage = Math.round((stats.correct / stats.total) * 100);
              return (
                <div key={domain} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex-1">
                    <div className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mb-2 ${getDomainColor(domain)}`}>
                      {domain}
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${percentage >= 70 ? 'bg-emerald-500' : 'bg-amber-500'}`}
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <span className={`text-sm font-semibold ${getScoreColor(percentage)}`}>{percentage}%</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {stats.correct} / {stats.total} correct
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {weakAreas.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-8 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <i className="ri-alert-line text-2xl text-amber-600"></i>
              <h2 className="text-xl font-bold text-slate-800">Areas to Review</h2>
            </div>
            <p className="text-sm text-slate-600 mb-4">Focus on these domains to improve your score:</p>
            <div className="space-y-3">
              {weakAreas.map(area => (
                <div key={area.domain} className="flex items-center justify-between p-4 bg-white rounded-lg border border-amber-200">
                  <div className={`px-3 py-1 rounded-full text-xs font-semibold ${getDomainColor(area.domain)}`}>
                    {area.domain}
                  </div>
                  <div className="text-sm text-slate-600">
                    {area.correct} / {area.total} correct ({area.percentage}%)
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 p-8 mb-8">
          <h2 className="text-xl font-bold text-slate-800 mb-4">Time Analysis</h2>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <i className="ri-time-line text-2xl text-indigo-600"></i>
              <div>
                <div className="text-sm font-semibold text-slate-700">Total Time Used</div>
                <div className="text-xs text-slate-500">Out of 134 minutes</div>
              </div>
            </div>
            <div className="text-2xl font-bold text-indigo-600">{formatTime(timeUsed)}</div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={onReview}
            className="flex-1 px-8 py-4 bg-gradient-to-r from-indigo-600 to-sky-600 text-white rounded-lg font-semibold hover:from-indigo-700 hover:to-sky-700 transition-all whitespace-nowrap"
          >
            <i className="ri-eye-line mr-2"></i>
            Review All Answers
          </button>
          <button
            onClick={onRetake}
            className="flex-1 px-8 py-4 bg-white border-2 border-slate-300 text-slate-700 rounded-lg font-semibold hover:bg-slate-50 transition-all whitespace-nowrap"
          >
            <i className="ri-restart-line mr-2"></i>
            Retake Exam
          </button>
          <Link
            to="/sat-prep/study-plan"
            className="flex-1 px-8 py-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg font-semibold hover:from-emerald-700 hover:to-teal-700 transition-all text-center whitespace-nowrap"
          >
            <i className="ri-calendar-line mr-2"></i>
            View Study Plan
          </Link>
        </div>
      </div>
    </div>
  );
}